import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { askNewa, getSystemConfig } from '../api';

// ─── Markdown renderer ─────────────────────────────────────────────────────
function MarkdownBlock({ text }) {
  const lines = text.split('\n');
  const elements = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Code block
    if (line.startsWith('```')) {
      const lang = line.replace('```', '').trim();
      const codeLines = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <div key={i} style={{ margin: '10px 0', borderRadius: '10px', overflow: 'hidden', border: '1px solid rgba(124,58,237,0.15)' }}>
          {lang && (
            <div style={{ background: 'rgba(124,58,237,0.1)', padding: '4px 12px', fontSize: '11px', fontWeight: 700, color: '#7C3AED', fontFamily: 'monospace' }}>
              {lang}
            </div>
          )}
          <pre style={{ margin: 0, padding: '14px 16px', background: '#1e1b2e', color: '#e2e8f0', fontSize: '13px', fontFamily: 'monospace', overflowX: 'auto', lineHeight: 1.6 }}>
            <code>{codeLines.join('\n')}</code>
          </pre>
        </div>
      );
      i++;
      continue;
    }

    // Table
    if (line.startsWith('|')) {
      const tableRows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        if (!lines[i].replace(/\|/g, '').replace(/-/g, '').trim() === '') {
          tableRows.push(lines[i]);
        }
        if (!lines[i].match(/^[\|\s\-:]+$/)) {
          tableRows.push(lines[i]);
        }
        i++;
      }
      const validRows = tableRows.filter(r => !r.match(/^[\|\s\-:]+$/));
      elements.push(
        <div key={i} style={{ overflowX: 'auto', margin: '10px 0' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '13px' }}>
            <tbody>
              {validRows.map((row, ri) => {
                const cells = row.split('|').filter(c => c.trim() !== '');
                return (
                  <tr key={ri} style={{ background: ri === 0 ? 'rgba(124,58,237,0.08)' : ri % 2 === 0 ? '#f9fafb' : '#fff' }}>
                    {cells.map((cell, ci) => ri === 0 ? (
                      <th key={ci} style={{ padding: '8px 14px', borderBottom: '2px solid rgba(124,58,237,0.2)', textAlign: 'left', fontWeight: 700, color: '#5B21B6', whiteSpace: 'nowrap' }}
                        dangerouslySetInnerHTML={{ __html: cell.trim().replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                    ) : (
                      <td key={ci} style={{ padding: '7px 14px', borderBottom: '1px solid #f0f0f5', color: '#374151' }}
                        dangerouslySetInnerHTML={{ __html: cell.trim().replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Headings
    if (line.startsWith('### ')) {
      elements.push(<h4 key={i} style={{ margin: '14px 0 6px', fontSize: '15px', fontWeight: 700, color: '#1F2937' }}>{renderInline(line.slice(4))}</h4>);
    } else if (line.startsWith('## ')) {
      elements.push(<h3 key={i} style={{ margin: '16px 0 8px', fontSize: '17px', fontWeight: 700, color: '#111827' }}>{renderInline(line.slice(3))}</h3>);
    } else if (line.startsWith('# ')) {
      elements.push(<h2 key={i} style={{ margin: '18px 0 10px', fontSize: '20px', fontWeight: 800, color: '#0F172A' }}>{renderInline(line.slice(2))}</h2>);
    }
    // Horizontal rule
    else if (line.match(/^---+$/)) {
      elements.push(<hr key={i} style={{ border: 'none', borderTop: '1px solid rgba(124,58,237,0.15)', margin: '12px 0' }} />);
    }
    // Bullet list
    else if (line.startsWith('- ') || line.startsWith('* ')) {
      elements.push(
        <div key={i} style={{ display: 'flex', gap: '8px', margin: '3px 0', fontSize: '14px', lineHeight: 1.65, color: '#374151' }}>
          <span style={{ color: '#7C3AED', fontWeight: 700, flexShrink: 0, marginTop: '2px' }}>•</span>
          <span dangerouslySetInnerHTML={{ __html: renderInlineHtml(line.slice(2)) }} />
        </div>
      );
    }
    // Numbered list
    else if (/^\d+\. /.test(line)) {
      const num = line.match(/^(\d+)\. /)[1];
      elements.push(
        <div key={i} style={{ display: 'flex', gap: '10px', margin: '3px 0', fontSize: '14px', lineHeight: 1.65, color: '#374151' }}>
          <span style={{ color: '#7C3AED', fontWeight: 700, flexShrink: 0, minWidth: '18px' }}>{num}.</span>
          <span dangerouslySetInnerHTML={{ __html: renderInlineHtml(line.replace(/^\d+\. /, '')) }} />
        </div>
      );
    }
    // Blockquote
    else if (line.startsWith('> ')) {
      elements.push(
        <div key={i} style={{ borderLeft: '3px solid #7C3AED', paddingLeft: '12px', margin: '8px 0', color: '#6B7280', fontSize: '14px', fontStyle: 'italic' }}>
          {renderInline(line.slice(2))}
        </div>
      );
    }
    // Empty line
    else if (!line.trim()) {
      elements.push(<div key={i} style={{ height: '8px' }} />);
    }
    // Normal paragraph
    else {
      elements.push(
        <p key={i} style={{ margin: '4px 0', fontSize: '14px', lineHeight: 1.7, color: '#374151' }}
          dangerouslySetInnerHTML={{ __html: renderInlineHtml(line) }} />
      );
    }
    i++;
  }
  return <div style={{ display: 'flex', flexDirection: 'column' }}>{elements}</div>;
}

function renderInlineHtml(text) {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong style="color:#111827;font-weight:700">$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/`(.*?)`/g, '<code style="background:rgba(124,58,237,0.1);color:#7C3AED;padding:2px 6px;border-radius:4px;font-family:monospace;font-size:12px">$1</code>')
    .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" style="color:#7C3AED;text-decoration:underline">$1</a>');
}

function renderInline(text) {
  return <span dangerouslySetInnerHTML={{ __html: renderInlineHtml(text) }} />;
}

// ─── Typing dots ────────────────────────────────────────────────────────────
function TypingDots() {
  return (
    <div style={{ display: 'flex', gap: '5px', alignItems: 'center', padding: '6px 0' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{
          width: '7px', height: '7px', borderRadius: '50%',
          background: 'linear-gradient(135deg, #7C3AED, #22c7a8)',
          animation: `nw-bounce 1.2s ease-in-out ${i * 0.2}s infinite`,
        }} />
      ))}
    </div>
  );
}

// ─── Module context map ──────────────────────────────────────────────────────
const MODULE_LABELS = {
  // crm: { label: 'CRM & Sales', icon: '📊', color: '#7C3AED', hint: 'Ask about leads, pipeline, deals, invoices…' },
  // finance: { label: 'Finance', icon: '💰', color: '#059669', hint: 'Ask about invoices, cash flow, revenue…' },
  // inventory: { label: 'Inventory', icon: '📦', color: '#D97706', hint: 'Ask about stock, suppliers, reorders…' },
  // hr: { label: 'HR & People', icon: '👥', color: '#2563EB', hint: 'Ask about employees, attendance, payroll…' },
  // reports: { label: 'Reports', icon: '📈', color: '#DB2777', hint: 'Ask about KPIs, autonomy score, trends…' },
  general: { label: 'General', icon: '✦', color: '#6B7280', hint: 'Ask anything about your business…' },
};

const SUGGESTED_PROMPTS = {
  // crm: [
  //   'Which leads should I focus on closing today?',
  //   'Show me overdue invoices and collection risks.',
  //   'How is our sales pipeline performing this month?',
  //   'Who is our top performing sales rep?',
  //   'Which marketing campaign has the best ROI?',
  // ],
  // finance: [
  //   'What is our current cash flow situation?',
  //   'Show me all overdue invoices above ₹50,000.',
  //   'What is our revenue trend for the last 6 months?',
  //   'Which customers have the highest outstanding balances?',
  // ],
  // inventory: [
  //   'Which products are running low on stock?',
  //   'Show me supplier delivery performance.',
  //   'What items need to be reordered this week?',
  //   'Which SKUs have the highest turnover rate?',
  // ],
  // hr: [
  //   'Who are our top performers this quarter?',
  //   'Show me attendance summary for this month.',
  //   'Which employees are at risk of burnout?',
  //   'What is the current headcount by department?',
  // ],
  // reports: [
  //   'What is my autonomy score and what drives it?',
  //   'Give me a full business health summary.',
  //   'What is the single biggest revenue opportunity right now?',
  //   'Show me a 90-day business action plan.',
  // ],
  general: [
    'Give me today\'s business summary.',
    'What needs my immediate attention?',
    'What is my overall business health score?',
    'Show me the most important KPIs across all modules.',
  ],
};

// ─── Storage helpers ─────────────────────────────────────────────────────────
const STORAGE_KEY = 'newa_conversations';
const MOBILE_BREAKPOINT = 768;

function loadConversations() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch { return []; }
}

function saveConversations(convs) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(convs.slice(0, 50))); } catch {}
}

function genId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2);
}

// ─── Main AskNEWA Workspace ──────────────────────────────────────────────────
export default function AskNewaWorkspace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Module context (from URL param or localStorage)
  const [module, setModule] = useState(() => {
    return searchParams.get('module') || localStorage.getItem('newa_last_module') || 'general';
  });

  // Conversations
  const [conversations, setConversations] = useState(loadConversations);
  const [activeConvId, setActiveConvId] = useState(() => {
    return localStorage.getItem('newa_last_active_conv_id') || null;
  });
  const [messages, setMessages] = useState([]);

  // Auto-restore last active conversation if present
  useEffect(() => {
    const savedId = localStorage.getItem('newa_last_active_conv_id');
    const convs = loadConversations();
    if (savedId && convs && convs.length > 0) {
      const found = convs.find(c => c.id === savedId);
      if (found) {
        setMessages(found.messages || []);
        setActiveConvId(found.id);
      } else if (convs[0]) {
        setMessages(convs[0].messages || []);
        setActiveConvId(convs[0].id);
      }
    } else if (convs && convs.length > 0 && !activeConvId) {
      setMessages(convs[0].messages || []);
      setActiveConvId(convs[0].id);
    }
  }, []);

  // Save activeConvId
  useEffect(() => {
    if (activeConvId) {
      localStorage.setItem('newa_last_active_conv_id', activeConvId);
    } else {
      localStorage.removeItem('newa_last_active_conv_id');
    }
  }, [activeConvId]);

  // Input
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Responsive sidebar: starts open on desktop, closed (drawer) on mobile
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false
  );
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth > MOBILE_BREAKPOINT : true
  );

  const [theme, setTheme] = useState(() => localStorage.getItem('newa_theme') || 'light');

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(false);
  const [tenantId, setTenantId] = useState('general');

  useEffect(() => {
    const fetchCompanyConfig = async () => {
      try {
        const response = await getSystemConfig();
        const tenantId = response?.data?.tenantId;
        console.log('config', tenantId);
        setTenantId(tenantId)
      } catch (error) {
        console.error('Failed to fetch company config:', error);

      }
    };
    fetchCompanyConfig();
  }, []);

  // Track viewport size and flip between docked sidebar (desktop) and drawer (mobile)
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(prevMobile => {
        if (prevMobile !== mobile) {
          // Only auto-toggle when crossing the breakpoint, not on every resize tick
          setSidebarOpen(!mobile);
        }
        return mobile;
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-submit from URL query (?q=...)
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setInput('');
      handleSend(q);
    }
    // eslint-disable-next-line
  }, []);

  // Save theme
  useEffect(() => {
    localStorage.setItem('newa_theme', theme);
  }, [theme]);

  // Scroll to bottom
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Keyboard shortcut Cmd/Ctrl+K → focus input
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        if (isMobile && sidebarOpen) {
          setSidebarOpen(false);
        } else {
          navigate(-1);
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [navigate, isMobile, sidebarOpen]);

  // Prevent body scroll bleed-through when the mobile drawer is open
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prevOverflow; };
    }
  }, [isMobile, sidebarOpen]);

  const handleSend = useCallback(async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || loading) return;
    setInput('');
    abortRef.current = false;

    // Add user message
    const userMsg = { role: 'user', text, timestamp: Date.now() };
    const updatedMsgs = [...messages, userMsg];
    setMessages(updatedMsgs);
    setLoading(true);

    // Prefix query with module context
    const contextPrefix = module !== 'general'
      ? `[Context: ${MODULE_LABELS[module]?.label || module} module] `
      : '';
    const fullQuery = contextPrefix + text;

    try {
      const res = await askNewa(fullQuery,tenantId);
      if (abortRef.current) return;

      const answer = res?.answer || res?.response || res?.message || res?.data?.answer
        || 'I received your question but could not generate a response. Please try again.';

      const newaMsg = { role: 'newa', text: answer, timestamp: Date.now() };
      const finalMsgs = [...updatedMsgs, newaMsg];
      setMessages(finalMsgs);

      // Persist conversation
      saveOrUpdateConversation(finalMsgs, text);
    } catch {
      if (!abortRef.current) {
        const errMsg = {
          role: 'newa',
          text: '⚠️ Unable to reach AskNEWA service. Please make sure the backend server is running at `http://localhost:8001`.',
          timestamp: Date.now(),
          isError: true,
        };

        setMessages(prev => [...prev, errMsg]);
      }
    } finally {
      if (!abortRef.current) setLoading(false);
    }
  }, [input, loading, messages, module]);

  function saveOrUpdateConversation(msgs, firstUserText) {
    const title = firstUserText.slice(0, 55) + (firstUserText.length > 55 ? '…' : '');
    setConversations(prev => {
      let updated;
      if (activeConvId) {
        updated = prev.map(c => c.id === activeConvId ? { ...c, messages: msgs, updatedAt: Date.now() } : c);
      } else {
        const newConv = { id: genId(), title, module, messages: msgs, createdAt: Date.now(), updatedAt: Date.now() };
        setActiveConvId(newConv.id);
        updated = [newConv, ...prev];
      }
      saveConversations(updated);
      return updated;
    });
  }

  function startNewChat() {
    setMessages([]);
    setActiveConvId(null);
    setInput('');
    setLoading(false);
    abortRef.current = false;
    if (isMobile) setSidebarOpen(false);
    inputRef.current?.focus();
  }

  function loadConversation(conv) {
    setMessages(conv.messages);
    setActiveConvId(conv.id);
    setModule(conv.module || 'general');
    setLoading(false);
    abortRef.current = false;
    if (isMobile) setSidebarOpen(false);
  }

  function deleteConversation(id, e) {
    e.stopPropagation();
    setConversations(prev => {
      const updated = prev.filter(c => c.id !== id);
      saveConversations(updated);
      return updated;
    });
    if (activeConvId === id) startNewChat();
  }

  function changeModule(mod) {
    setModule(mod);
    localStorage.setItem('newa_last_module', mod);
    if (isMobile) setSidebarOpen(false);
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const stopGenerating = () => {
    abortRef.current = true;
    setLoading(false);
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const isDark = theme === 'dark';
  const modInfo = MODULE_LABELS[module] || MODULE_LABELS.general;
  const suggestions = SUGGESTED_PROMPTS[module] || SUGGESTED_PROMPTS.general;
  const isEmptyChat = messages.length === 0;

  // ── Colors ──────────────────────────────────────────────────────────────
  const bg = isDark ? '#0F0E1A' : '#F7F8FC';
  const sidebarBg = isDark ? '#13111f' : '#1e1637';
  const chatBg = isDark ? '#0F0E1A' : '#ffffff';
  const cardBg = isDark ? '#1a1730' : '#ffffff';
  const textPrimary = isDark ? '#F1F0FF' : '#0F172A';
  const textSecondary = isDark ? '#9CA3AF' : '#6B7280';
  const border = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)';
  const inputBg = isDark ? '#1e1b2e' : '#F9FAFB';

  return (
    <div style={{ width: '100vw', height: '100vh', display: 'flex', background: bg, fontFamily: 'Inter, system-ui, sans-serif', overflow: 'hidden' }}>

      {/* ── CSS ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        @keyframes nw-bounce { 0%,80%,100%{transform:scale(0.6);opacity:0.4} 40%{transform:scale(1);opacity:1} }
        @keyframes nw-fadein { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes nw-pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
        @keyframes nw-overlay-in { from{opacity:0} to{opacity:1} }
        .nw-conv-item:hover { background: rgba(124,58,237,0.12) !important; }
        .nw-conv-item.active { background: rgba(124,58,237,0.2) !important; }
        .nw-module-btn:hover { background: rgba(255,255,255,0.1) !important; }
        .nw-module-btn.active { background: rgba(124,58,237,0.25) !important; border-color: rgba(124,58,237,0.5) !important; }
        .nw-send-btn:hover:not(:disabled) { transform: scale(1.05); box-shadow: 0 4px 16px rgba(124,58,237,0.4); }
        .nw-suggest:hover { background: rgba(124,58,237,0.1) !important; border-color: rgba(124,58,237,0.4) !important; transform: translateY(-1px); }
        .nw-msg { animation: nw-fadein 0.3s ease; }
        .nw-del-btn { opacity: 0; transition: opacity 0.15s; }
        .nw-conv-item:hover .nw-del-btn { opacity: 1 !important; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(124,58,237,0.25); border-radius: 4px; }
        textarea { resize: none; }

        @media (max-width: 768px) {
          .nw-suggestions-grid { grid-template-columns: 1fr !important; }
          .nw-topbar { padding-left: 12px !important; padding-right: 12px !important; gap: 8px !important; }
          .nw-chat-scroll { padding-left: 12px !important; padding-right: 12px !important; }
          .nw-bottom-bar { padding-left: 12px !important; padding-right: 12px !important; }
          .nw-hint-text { display: none !important; }
        }
      `}</style>

      {/* ════════════════ SIDEBAR (docked on desktop, drawer on mobile) ════════════════ */}

      {/* Backdrop — only rendered on mobile while the drawer is open */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,14,26,0.55)',
            zIndex: 40, animation: 'nw-overlay-in 0.2s ease',
          }}
        />
      )}

      {sidebarOpen && (
        <div style={{
          width: isMobile ? '82vw' : '260px',
          maxWidth: isMobile ? '300px' : 'none',
          flexShrink: 0,
          background: sidebarBg,
          display: 'flex', flexDirection: 'column',
          borderRight: `1px solid rgba(255,255,255,0.06)`,
          transition: isMobile ? 'transform 0.25s ease' : 'width 0.25s',
          ...(isMobile
            ? {
                position: 'fixed', top: 0, left: 0, bottom: 0,
                zIndex: 50,
                transform: 'translateX(0)',
                boxShadow: '6px 0 28px rgba(0,0,0,0.3)',
              }
            : {}),
        }}>

          {/* Logo + close */}
          <div style={{ padding: '16px 14px 12px', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: 'rgba(255,255,255,0.06) 1px solid' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'linear-gradient(135deg, #7C3AED, #22c7a8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 }}>✦</div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '14px', color: '#fff', letterSpacing: '0.02em' }}>AskNEWA</div>
              <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.45)', marginTop: '1px' }}>AI Business Assistant</div>
            </div>
            <button onClick={() => setSidebarOpen(false)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: '2px 4px' }} title={isMobile ? 'Close menu' : 'Collapse sidebar'}>
              {isMobile ? '✕' : '‹'}
            </button>
          </div>

          {/* New Chat */}
          <div style={{ padding: '10px 12px 8px' }}>
            <button
              onClick={startNewChat}
              style={{ width: '100%', background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', border: 'none', borderRadius: '10px', padding: '9px 14px', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', transition: 'opacity 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              <span style={{ fontSize: '16px' }}>＋</span> New Chat
            </button>
          </div>

          {/* Module Filters */}
          <div style={{ padding: '0 12px 6px' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px', paddingLeft: '4px' }}>Modules</div>
            {Object.entries(MODULE_LABELS).map(([key, info]) => (
              <button
                key={key}
                className={`nw-module-btn ${module === key ? 'active' : ''}`}
                onClick={() => changeModule(key)}
                style={{ width: '100%', background: 'transparent', border: '1px solid transparent', borderRadius: '8px', padding: '7px 10px', color: module === key ? '#fff' : 'rgba(255,255,255,0.55)', fontSize: '12.5px', fontWeight: module === key ? 600 : 400, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '9px', textAlign: 'left', marginBottom: '2px', transition: 'all 0.15s' }}
              >
                <span style={{ fontSize: '14px' }}>{info.icon}</span>
                {info.label}
                {module === key && <span style={{ marginLeft: 'auto', width: '6px', height: '6px', borderRadius: '50%', background: info.color }} />}
              </button>
            ))}
          </div>

          {/* Conversation History */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px' }}>
            {conversations.length > 0 && (
              <>
                <div style={{ fontSize: '10px', fontWeight: 700, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px', paddingLeft: '4px', marginTop: '10px' }}>History</div>
                {conversations.map(conv => (
                  <div
                    key={conv.id}
                    className={`nw-conv-item ${conv.id === activeConvId ? 'active' : ''}`}
                    onClick={() => loadConversation(conv)}
                    style={{ padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', marginBottom: '2px', transition: 'background 0.15s', display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}
                  >
                    <span style={{ fontSize: '13px', flexShrink: 0 }}>{MODULE_LABELS[conv.module]?.icon || '✦'}</span>
                    <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                      {conv.title}
                    </span>
                    <button
                      className="nw-del-btn"
                      onClick={(e) => deleteConversation(conv.id, e)}
                      style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', fontSize: '13px', padding: '0 2px', flexShrink: 0 }}
                      title="Delete"
                    >✕</button>
                  </div>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '10px 14px', borderTop: 'rgba(255,255,255,0.06) 1px solid' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')}
                style={{ background: 'rgba(255,255,255,0.07)', border: 'none', borderRadius: '8px', padding: '6px 10px', color: 'rgba(255,255,255,0.55)', fontSize: '13px', cursor: 'pointer' }}
                title="Toggle theme"
              >
                {isDark ? '☀️' : '🌙'}
              </button>
              <button
                onClick={() => navigate(-1)}
                style={{ background: 'rgba(255,255,255,0.07)', border: 'none', borderRadius: '8px', padding: '6px 10px', color: 'rgba(255,255,255,0.55)', fontSize: '12px', cursor: 'pointer', flex: 1 }}
              >
                ← Back to IRP
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════ MAIN AREA ════════════════ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: chatBg }}>

        {/* ── Top Bar ── */}
        <div className="nw-topbar" style={{ height: '56px', padding: '0 20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: `1px solid ${border}`, flexShrink: 0, background: chatBg }}>
          {!sidebarOpen && (
            <button onClick={() => setSidebarOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textSecondary, fontSize: '20px', padding: '4px 8px', borderRadius: '6px', flexShrink: 0 }} title="Open menu">☰</button>
          )}

          {/* Module badge */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '5px 12px', background: `${modInfo.color}15`, borderRadius: '999px', border: `1px solid ${modInfo.color}30`, minWidth: 0 }}>
            <span style={{ fontSize: '14px', flexShrink: 0 }}>{modInfo.icon}</span>
            <span style={{ fontSize: '12px', fontWeight: 600, color: modInfo.color, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{modInfo.label}</span>
          </div>

          <div style={{ flex: 1 }} />

          {/* Keyboard hint — hidden on mobile via .nw-hint-text */}
          <div className="nw-hint-text" style={{ fontSize: '11px', color: textSecondary, alignItems: 'center', gap: '5px', display: 'flex' }}>
            <kbd style={{ background: isDark ? 'rgba(255,255,255,0.08)' : '#F3F4F6', border: `1px solid ${border}`, borderRadius: '4px', padding: '2px 6px', fontSize: '10px', color: textSecondary }}>Ctrl K</kbd>
            to focus
          </div>

          <button onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', padding: '4px', flexShrink: 0 }} title="Toggle theme">
            {isDark ? '☀️' : '🌙'}
          </button>

          <button onClick={() => navigate(-1)} style={{ background: isDark ? 'rgba(255,255,255,0.08)' : '#F3F4F6', border: `1px solid ${border}`, borderRadius: '8px', padding: '6px 14px', fontSize: '12px', fontWeight: 600, color: textSecondary, cursor: 'pointer', flexShrink: 0 }}>
            ✕ Close
          </button>
        </div>

        {/* ── Chat Area ── */}
        <div className="nw-chat-scroll" style={{ flex: 1, overflowY: 'auto', padding: '0 20px' }}>
          <div style={{ maxWidth: '760px', margin: '0 auto', paddingBottom: '20px' }}>

            {/* ─ Welcome Screen ─ */}
            {isEmptyChat && (
              <div style={{ paddingTop: '60px', textAlign: 'center', animation: 'nw-fadein 0.5s ease' }}>
                <div style={{ width: '72px', height: '72px', borderRadius: '20px', background: 'linear-gradient(135deg, #7C3AED, #22c7a8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '34px', margin: '0 auto 20px', boxShadow: '0 12px 32px rgba(124,58,237,0.3)' }}>✦</div>
                <h1 style={{ fontSize: '28px', fontWeight: 800, color: textPrimary, marginBottom: '8px', letterSpacing: '-0.02em' }}>
                  Ask NEWA
                </h1>
                <p style={{ fontSize: '15px', color: textSecondary, marginBottom: '8px' }}>
                  Your AI business intelligence assistant for the iXES ecosystem.
                </p>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', background: `${modInfo.color}15`, borderRadius: '999px', marginBottom: '40px' }}>
                  <span style={{ fontSize: '13px' }}>{modInfo.icon}</span>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: modInfo.color }}>Active: {modInfo.label}</span>
                </div>

                {/* Suggestions */}
                <div style={{ textAlign: 'left' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, color: textSecondary, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '12px' }}>
                    Suggested for {modInfo.label}
                  </div>
                  <div className="nw-suggestions-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {suggestions.map((s, i) => (
                      <button
                        key={i}
                        className="nw-suggest"
                        onClick={() => handleSend(s)}
                        style={{
                          background: cardBg,
                          border: `1px solid ${border}`,
                          borderRadius: '12px',
                          padding: '14px 16px',
                          cursor: 'pointer',
                          textAlign: 'left',
                          fontSize: '13px',
                          color: textPrimary,
                          lineHeight: 1.5,
                          transition: 'all 0.18s',
                          boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.04)',
                        }}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ─ Messages ─ */}
            {messages.map((msg, idx) => (
              <div key={idx} className="nw-msg" style={{ marginTop: '24px' }}>
                {msg.role === 'user' ? (
                  /* User message */
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <div style={{ maxWidth: '75%', background: 'linear-gradient(135deg, #7C3AED, #5B21B6)', color: '#fff', borderRadius: '18px 18px 4px 18px', padding: '12px 18px', fontSize: '14px', lineHeight: 1.65, boxShadow: '0 4px 16px rgba(124,58,237,0.25)' }}>
                      {msg.text}
                    </div>
                  </div>
                ) : (
                  /* NEWA message */
                  <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'linear-gradient(135deg, #7C3AED, #22c7a8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0, boxShadow: '0 4px 12px rgba(124,58,237,0.3)', marginTop: '2px' }}>✦</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: '#7C3AED', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>NEWA</div>
                      <div style={{
                        background: msg.isError ? (isDark ? '#2D1515' : '#FEF2F2') : (isDark ? '#1a1730' : '#F9FAFB'),
                        border: `1px solid ${msg.isError ? '#FECACA' : border}`,
                        borderRadius: '4px 18px 18px 18px',
                        padding: '16px 18px',
                        boxShadow: isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.04)',
                      }}
                        onClick={(e) => {

                          const link = e.target.closest('a');
                          if (link) {
                            const href = link.getAttribute('href');
                            if (href && (href.startsWith('/crm') || href.startsWith('/'))) {
                              e.preventDefault();
                              navigate(href);
                            }
                          }
                        }}
                      >
                        <MarkdownBlock text={msg.text} />
                      </div>

                      <div style={{ fontSize: '11px', color: textSecondary, marginTop: '6px', paddingLeft: '4px' }}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div className="nw-msg" style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', marginTop: '24px' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'linear-gradient(135deg, #7C3AED, #22c7a8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0, boxShadow: '0 4px 12px rgba(124,58,237,0.3)', animation: 'nw-pulse 2s ease infinite' }}>✦</div>
                <div style={{ background: isDark ? '#1a1730' : '#F9FAFB', border: `1px solid ${border}`, borderRadius: '4px 18px 18px 18px', padding: '16px 20px' }}>
                  <TypingDots />
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>
        </div>

        {/* ── Bottom Input ── */}
        <div className="nw-bottom-bar" style={{ padding: '14px 20px 20px', borderTop: `1px solid ${border}`, background: chatBg, flexShrink: 0 }}>
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>

            {/* Stop button */}
            {loading && (
              <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                <button
                  onClick={stopGenerating}
                  style={{ background: 'none', border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.2)' : '#E5E7EB'}`, borderRadius: '20px', padding: '6px 18px', fontSize: '12px', fontWeight: 600, color: textSecondary, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                >
                  <span style={{ width: '8px', height: '8px', background: '#EF4444', borderRadius: '2px', display: 'inline-block' }} />
                  Stop generating
                </button>
              </div>
            )}

            <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', gap: '10px', background: inputBg, border: `1.5px solid ${isDark ? 'rgba(124,58,237,0.3)' : 'rgba(124,58,237,0.25)'}`, borderRadius: '16px', padding: '12px 14px', boxShadow: isDark ? '0 0 0 1px rgba(124,58,237,0.1)' : '0 4px 24px rgba(0,0,0,0.06)', transition: 'border-color 0.2s, box-shadow 0.2s' }}
              onFocusCapture={e => e.currentTarget.style.borderColor = '#7C3AED'}
              onBlurCapture={e => e.currentTarget.style.borderColor = isDark ? 'rgba(124,58,237,0.3)' : 'rgba(124,58,237,0.25)'}
            >
              {/* Module indicator */}
              <div style={{ flexShrink: 0, fontSize: '18px', paddingBottom: '2px' }}>{modInfo.icon}</div>

              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={modInfo.hint}
                disabled={loading}
                rows={1}
                style={{
                  flex: 1, background: 'transparent', border: 'none', outline: 'none',
                  fontSize: '14px', color: textPrimary, fontFamily: 'inherit',
                  lineHeight: 1.6, maxHeight: '180px', overflowY: 'auto',
                  minHeight: '24px',
                }}
                onInput={e => {
                  e.target.style.height = 'auto';
                  e.target.style.height = Math.min(e.target.scrollHeight, 180) + 'px';
                }}
              />

              {/* Send button */}
              <button
                className="nw-send-btn"
                onClick={() => handleSend()}
                disabled={loading || !input.trim()}
                style={{
                  flexShrink: 0,
                  width: '38px', height: '38px',
                  background: loading || !input.trim() ? (isDark ? 'rgba(124,58,237,0.2)' : '#E5E7EB') : 'linear-gradient(135deg, #7C3AED, #22c7a8)',
                  border: 'none', borderRadius: '10px', cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={loading || !input.trim() ? '#9CA3AF' : '#fff'} strokeWidth="2.5">
                  <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>

            <div className="nw-hint-text" style={{ textAlign: 'center', marginTop: '8px', fontSize: '11px', color: textSecondary }}>
              <kbd style={{ background: isDark ? 'rgba(255,255,255,0.08)' : '#F3F4F6', border: `1px solid ${border}`, borderRadius: '3px', padding: '1px 5px', fontSize: '10px' }}>Enter</kbd> to send &nbsp;·&nbsp;
              <kbd style={{ background: isDark ? 'rgba(255,255,255,0.08)' : '#F3F4F6', border: `1px solid ${border}`, borderRadius: '3px', padding: '1px 5px', fontSize: '10px' }}>Shift+Enter</kbd> for new line &nbsp;·&nbsp;
              Powered by iXES AskNEWA AI
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}